import { useMutation, useQueryClient } from "@tanstack/react-query";
import { chatApi } from "../../api/chat";
import { UpdateMessageRequest } from "../../types/sendMessage";
import { ChatMessage } from "../../types/chat";

interface InfiniteMessagesData {
  pages: Array<{
    messages: ChatMessage[];
    iterator?: unknown;
  }>;
  pageParams: unknown[];
}

/**
 * Hook để cập nhật tin nhắn hiện có
 * @param roomId - ID phòng chat cho API call
 * @param threadId - ID thread cho cache invalidation (phải khớp với query key của useInfiniteMessages)
 * @returns Mutation object với hàm updateMessage
 */
export const useUpdateMessage = (roomId: string, threadId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, content, metadata }: UpdateMessageRequest) => {
      const response = await chatApi.updateMessage(roomId, messageId, content, metadata);
      return response;
    },

    onMutate: async ({ messageId, content }) => {
      // Sử dụng threadId cho cache key để khớp với useInfiniteMessages
      const queryKey = ["infinite-messages", threadId];

      // Hủy các refetch đang chạy để tránh ghi đè optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Lưu snapshot giá trị trước đó để rollback
      const previousMessages = queryClient.getQueryData(queryKey);

      // Cập nhật cache optimistically
      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    content: { ...msg.content, message: content },
                    editedOn: new Date().toISOString(),
                  }
                : msg,
            ),
          })),
        };
      });

      return { previousMessages, queryKey };
    },

    onError: (err, variables, context) => {
      // Rollback khi có lỗi
      if (context?.previousMessages && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousMessages);
      }
      console.error("Cập nhật tin nhắn thất bại:", err);
    },

    onSuccess: (data, variables, context) => {
      // Invalidate để tải lại từ server và đảm bảo tính nhất quán
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });
};
